import {
  Entity,
  schema,
  string,
  boolean,
  Table,
  InputItem,
  QueryCommand,
  GetItemCommand,
  list,
  map,
} from 'dynamodb-toolbox'
import _ from 'lodash'
import myzod, { Infer } from 'myzod'
import { createId } from '../../../helpers/utils'
import { ApplicationTable } from '../../tables/applicationDynamoDbTable'

export enum UserRoles {
  USER = 'User',
  Jobber = 'Jobber',
  Admin = 'Admin',
}

export enum UserGroups {
  ND = 'NotDefined',
}

export enum Timezones {
  HALIFAX = 'America/Halifax',
  WINNIPEG = 'America/Winnipeg',
  TORONTO = 'America/Toronto',
  EDMONTON = 'America/Edmonton',
  ST_JOHNS = 'America/St_Johns',
  VANCOUVER = 'America/Vancouver',
  REGINA = 'America/Regina',
}

export const Name = 'User'

// Define the DynamoDB schema
const baseUserSchema = schema({
  pk: string().putDefault('Users').key().hidden().optional(),
  id: string()
    .putDefault(() => createId(Name))
    .key()
    .optional(),
  email: string().required(),
  cognitoUserId: string().required(),
  stripeId: string().required(),
  phoneNumber: string().required(),
  enabled: boolean().required(),
  address: map({
    city: string(),
    province: string(),
    streetAddress: string(),
    unitNumber: string().optional(),
    postalCode: string().optional(),
  }).optional(),
  firstName: string().required(),
  lastName: string().required(),
  roles: list(string().enum('User', 'Jobber', 'Admin')).required(),
  groups: list(string()).default([]).required(),
  externalIds: list(map({ id: string(), source: string() })).required(),

  timezone: string()
    .default(() => Timezones.EDMONTON)
    .optional(),
  type: string()
    .default(() => Name)
    .optional(),
})

const userSchemaWithGsi = baseUserSchema.and({
  gsi_1_pk: string()
    .hidden()
    .default(() => 'userByEmail'),
  gsi_1_sk: string()
    .hidden()
    .link<typeof baseUserSchema>(({ email }) => email.toUpperCase()),

  gsi_2_pk: string()
    .hidden()
    .default(() => 'userByCognitoId'),
  gsi_2_sk: string()
    .hidden()
    .link<typeof baseUserSchema>(({ cognitoUserId }) => cognitoUserId),

  gsi_3_pk: string()
    .hidden()
    .default(() => 'userByStripeId'),
  gsi_3_sk: string()
    .hidden()
    .link<typeof baseUserSchema>(({ stripeId }) => stripeId),

  gsi_4_pk: string()
    .hidden()
    .link<typeof baseUserSchema>(({ phoneNumber }) => phoneNumber),
  gsi_4_sk: string()
    .hidden()
    .optional()
    .link<typeof baseUserSchema>(({ id }) => id),

  gsi_9_pk: string()
    .hidden()
    .default(() => Name)
    .optional(),

  gsi_9_sk: string()
    .hidden()
    .optional()
    .link<typeof baseUserSchema>(({ id }) => id),
})

// Define the `Entity` using `dynamodb-toolbox`
export const getUserEntity = (table: Table) => {
  return new Entity({
    table,
    name: Name,
    schema: userSchemaWithGsi,
    computeKey: ({ id }) => ({
      pk: 'Users',
      sk: id as string,
    }),
  })
}

export const UserEntity = getUserEntity(ApplicationTable)
export type UserInputEntity = InputItem<typeof UserEntity>

// Define the `myzod` schema for runtime validation
export const UserId = myzod.string().pattern(/^User#/)
export const EmailSchema = myzod.string().pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
export const phoneNumberSchema = myzod.string().pattern(/^\+\d+$/)
export const AddressSchema = myzod.object({
  city: myzod.string(),
  province: myzod.string(),
  streetAddress: myzod.string(),
  unitNumber: myzod.string().optional(),
  postalCode: myzod.string().optional(),
})

export const UserEntitySchema = myzod
  .object({
    id: UserId,
    email: EmailSchema,
    cognitoUserId: myzod.string(),
    stripeId: myzod.string(),
    phoneNumber: phoneNumberSchema,
    enabled: myzod.boolean(),
    address: AddressSchema.optional(),
    firstName: myzod.string(),
    lastName: myzod.string(),
    roles: myzod.array(myzod.enum(UserRoles)),
    groups: myzod.array(myzod.enum(UserGroups)),
    externalIds: myzod.array(
      myzod.object({ id: myzod.string(), source: myzod.string() }),
    ),
    timezone: myzod.enum(Timezones).optional(),
  })
  .allowUnknownKeys(true)

export type UserEntityType = Infer<typeof UserEntitySchema>

// Functions

export const getUserById = async (id: string) => {
  const userRec = await UserEntity.build(GetItemCommand)
    .key({
      id,
    })
    .send()
  return userRec.Item
}

export const getUserByEmail = async (
  email: string,
  table: Table = ApplicationTable,
) => {
  EmailSchema.parse(email)

  const query = table
    .build(QueryCommand)
    .query({
      index: 'gsi1',
      partition: 'userByEmail',
      range: { eq: email.toUpperCase() },
    })
    .entities(UserEntity)
  const { Items: users = [] } = await query.send()

  return users && users.length > 0
    ? UserEntitySchema.parse(users[0])
    : undefined
}

export const getUserByCognitoId = async (
  cognitoId: string,
  table: Table = ApplicationTable,
) => {
  const query = table
    .build(QueryCommand)
    .query({
      index: 'gsi2',
      partition: 'userByCognitoId',
      range: { eq: cognitoId },
    })
    .entities(UserEntity)
  const { Items: users = [] } = await query.send()
  return users && users.length > 0
    ? UserEntitySchema.parse(users[0])
    : undefined
}

export const getUserByStripeId = async (
  stripeId: string,
  table: Table = ApplicationTable,
) => {
  const query = table
    .build(QueryCommand)
    .query({
      index: 'gsi3',
      partition: 'userByStripeId',
      range: { eq: stripeId },
    })
    .entities(UserEntity)
  const { Items: users = [] } = await query.send()
  return users && users.length > 0
    ? UserEntitySchema.parse(users[0])
    : undefined
}

export const getUserByPhoneNumber = async (
  phoneNumber: string,
  table: Table = ApplicationTable,
) => {
  const query = table
    .build(QueryCommand)
    .query({
      index: 'gsi4',
      partition: phoneNumber,
      range: { beginsWith: `${Name}#` },
    })
    .entities(UserEntity)
  const { Items: users = [] } = await query.send()
  return users && users.length > 0
    ? UserEntitySchema.parse(users[0])
    : undefined
}

export const getUsersExternalId = (
  user: UserEntityType,
  source: string,
): string | undefined => {
  const userExternalIds = _.groupBy(user.externalIds, 'source')
  return userExternalIds?.[source]?.[0]?.id
}
